import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lightning, Funnel, CaretLeft, CaretRight } from "@phosphor-icons/react";

const ACTION_COLORS = {
  create: "#10B981", update: "#3B82F6", delete: "#EF4444", login: "#6B7280",
  register: "#8B5CF6", send: "#F59E0B", schedule: "#06B6D4", toggle: "#C5A059",
};

function getActionColor(action) {
  for (const [key, color] of Object.entries(ACTION_COLORS)) {
    if (action?.includes(key)) return color;
  }
  return "#6B7280";
}

export default function ActivityLogPage() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "", resource_type: "" });
  const limit = 30;

  useEffect(() => { loadActivities(); }, [page, filters]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (filters.action) params.set("action", filters.action);
      if (filters.resource_type) params.set("resource_type", filters.resource_type);
      const { data } = await api.get(`/dashboard/activity-log?${params}`);
      setActivities(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load activity log"); }
    finally { setLoading(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const RESOURCE_TYPES = ["", "user", "company", "candidate", "job", "lead", "team", "vacancy", "placement", "interview", "email", "invoice", "feature_toggle", "talent_pool", "search"];

  return (
    <div data-testid="activity-log-page" className="space-y-4">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Activity Log</h1>
        <p className="text-sm text-[#6B7280] mt-0.5">Complete audit trail of all actions</p>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="activity-filters">
        <input type="text" placeholder="Search action..." className="border rounded-lg px-3 py-1.5 text-xs w-40"
          value={filters.action} onChange={e => { setFilters({ ...filters, action: e.target.value }); setPage(1); }} />
        <select className="border rounded-lg px-3 py-1.5 text-xs"
          value={filters.resource_type} onChange={e => { setFilters({ ...filters, resource_type: e.target.value }); setPage(1); }}>
          {RESOURCE_TYPES.map(rt => <option key={rt} value={rt}>{rt || "All Resources"}</option>)}
        </select>
        <span className="text-xs text-[#6B7280] self-center">{total} total records</span>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-14 skeleton-shimmer rounded-lg" />)}</div>
      ) : activities.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <Lightning size={48} className="text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-[#6B7280]">No activity logged yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left py-2.5 px-4 text-[#6B7280] font-medium">Time</th>
                <th className="text-left py-2.5 px-4 text-[#6B7280] font-medium">User</th>
                <th className="text-left py-2.5 px-4 text-[#6B7280] font-medium">Action</th>
                <th className="text-left py-2.5 px-4 text-[#6B7280] font-medium">Resource</th>
                <th className="text-left py-2.5 px-4 text-[#6B7280] font-medium hidden md:table-cell">Details</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((a, i) => (
                <tr key={a.id || i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`activity-row-${i}`}>
                  <td className="py-2.5 px-4 text-[#6B7280] whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</td>
                  <td className="py-2.5 px-4 font-medium text-[#0F172A]">{a.user_name || a.user_id?.slice(0, 8) || "System"}</td>
                  <td className="py-2.5 px-4">
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold text-white" style={{ backgroundColor: getActionColor(a.action) }}>
                      {a.action?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-[#1E3A8A] capitalize">{a.resource_type}</td>
                  <td className="py-2.5 px-4 text-[#6B7280] hidden md:table-cell max-w-[200px] truncate">
                    {a.details ? JSON.stringify(a.details).slice(0, 60) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280]">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="gap-1"><CaretLeft size={12} /> Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="gap-1">Next <CaretRight size={12} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
